import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  GraduationCap, Loader2, CheckCircle2, XCircle, MailWarning,
  LogIn, UserPlus, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusInvitacionEmail() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);

  const authToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
  let userLogged = null;
  try { userLogged = userStr ? JSON.parse(userStr) : null; } catch { userLogged = null; }

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/campus/invitaciones-email/${token}`);
        if (!cancel) setInfo(res.data);
      } catch (err) {
        if (!cancel) setErrorMsg(err.response?.data?.detail || 'No pudimos cargar la invitación.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const guardarRedirectYIr = (destino) => {
    try {
      sessionStorage.setItem('redirectAfterLogin', `/campus/invitacion-email/${token}`);
    } catch { /* ignore */ }
    navigate(destino);
  };

  const aceptar = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API_URL}/campus/invitaciones-email/${token}/aceptar`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAcceptResult({ ok: true, message: res.data.message, space_id: res.data.space_id });
    } catch (err) {
      setAcceptResult({ ok: false, message: err.response?.data?.detail || 'No pudimos aceptar la invitación.' });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-10">
            <Loader2 className="w-10 h-10 mx-auto text-violet-600 animate-spin mb-3" />
            <p className="text-gray-600">Cargando invitación...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="invitacion-error">
          <CardContent className="text-center py-10 px-6">
            <XCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitación no disponible</h1>
            <p className="text-gray-600 mb-6">{errorMsg}</p>
            <Button onClick={() => navigate('/')} variant="outline">Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { estado, email, space, mensaje_personalizado, invitado_por_nombre, usuario_existente, usuario_tipo } = info || {};
  const tipoLabel = space?.tipo === 'curso' ? 'curso' : 'comisión';
  const colorTema = space?.color_tema || '#7c3aed';
  const esComision = space?.tipo === 'comision';

  // Estados terminales
  if (estado === 'expirada' || estado === 'cancelada') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="invitacion-no-valida">
          <CardContent className="text-center py-10 px-6">
            <MailWarning className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {estado === 'expirada' ? 'Esta invitación expiró' : 'Esta invitación fue cancelada'}
            </h1>
            <p className="text-gray-600 mb-6">
              Pedile al coordinador del espacio que te envíe una nueva invitación.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (estado === 'aceptada' || acceptResult?.ok) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="invitacion-aceptada">
          <CardContent className="text-center py-10 px-6">
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-500 mb-3" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">¡Listo!</h1>
            <p className="text-gray-600 mb-6">
              {acceptResult?.message || `Ya formás parte de ${space?.nombre || 'este espacio'}.`}
            </p>
            <div className="flex gap-2 justify-center">
              {authToken && (
                <Button
                  onClick={() => navigate(`/campus/espacio/${acceptResult?.space_id || space?.id}`)}
                  style={{ backgroundColor: colorTema }}
                  className="text-white"
                  data-testid="ir-al-espacio-btn"
                >
                  Ir al espacio
                </Button>
              )}
              <Button onClick={() => navigate('/campus')} variant="outline">
                Mi Campus
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // estado === pendiente
  const emailDelLogueado = (userLogged?.email || '').toLowerCase();
  const emailCoincide = emailDelLogueado && emailDelLogueado === (email || '').toLowerCase();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full" data-testid="invitacion-pendiente">
        <CardContent className="py-10 px-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${colorTema}20` }}
          >
            <GraduationCap className="w-8 h-8" style={{ color: colorTema }} />
          </div>

          <div className="text-center mb-6">
            <Badge variant="outline" className="mb-3" style={{ borderColor: colorTema, color: colorTema }}>
              {esComision ? 'Comisión' : 'Curso'} · Campus CATSLZ
            </Badge>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Te invitamos a sumarte a {space?.nombre}
            </h1>
            {invitado_por_nombre && (
              <p className="text-sm text-gray-500">
                Te invitó <strong>{invitado_por_nombre}</strong>
              </p>
            )}
          </div>

          {mensaje_personalizado && (
            <div className="bg-gray-50 border-l-4 p-3 mb-6 rounded" style={{ borderColor: colorTema }}>
              <p className="text-sm text-gray-700 italic">"{mensaje_personalizado}"</p>
            </div>
          )}

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-6 text-sm">
            <div className="flex items-center gap-2 text-violet-900">
              <Users className="w-4 h-4" />
              <span>Invitación enviada a <strong data-testid="invitacion-email">{email}</strong></span>
            </div>
          </div>

          {acceptResult?.ok === false && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
              {acceptResult.message}
            </div>
          )}

          {/* CTAs según estado de auth */}
          {authToken && emailCoincide ? (
            <div className="space-y-2">
              <Button
                onClick={aceptar}
                disabled={accepting}
                className="w-full text-white"
                style={{ backgroundColor: colorTema }}
                data-testid="aceptar-invitacion-btn"
              >
                {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Unirme al espacio
              </Button>
            </div>
          ) : authToken && !emailCoincide ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
                Estás ingresade como <strong>{userLogged?.email}</strong>, pero la invitación es para <strong>{email}</strong>.
                Cerrá sesión e ingresá con la cuenta correcta.
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  guardarRedirectYIr(usuario_tipo === 'externo' ? `/campus/acceso?email=${encodeURIComponent(email)}` : `/login`);
                }}
                className="w-full"
                data-testid="cambiar-cuenta-btn"
              >
                Cerrar sesión y cambiar de cuenta
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {usuario_existente ? (
                <>
                  <p className="text-sm text-gray-600 text-center mb-2">
                    Ya tenés una cuenta. Iniciá sesión para aceptar la invitación.
                  </p>
                  <Button
                    onClick={() => guardarRedirectYIr(
                      usuario_tipo === 'externo'
                        ? `/campus/acceso?email=${encodeURIComponent(email)}`
                        : `/login`
                    )}
                    className="w-full text-white"
                    style={{ backgroundColor: colorTema }}
                    data-testid="iniciar-sesion-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Iniciar sesión
                  </Button>
                </>
              ) : (
                <>
                  {esComision ? (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm mb-3">
                      Las comisiones son sólo para matriculades del Colegio. Ingresá con tu cuenta de matriculade.
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 text-center mb-2">
                      ¿No tenés cuenta? Creá una rápidamente para sumarte al espacio.
                    </p>
                  )}

                  {!esComision && (
                    <Button
                      onClick={() => guardarRedirectYIr(`/campus/acceso?tab=registro&email=${encodeURIComponent(email)}`)}
                      className="w-full text-white"
                      style={{ backgroundColor: colorTema }}
                      data-testid="crear-cuenta-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Crear cuenta y unirme
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => guardarRedirectYIr(`/login`)}
                    className="w-full"
                    data-testid="iniciar-sesion-matriculado-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Ya soy matriculade · Iniciar sesión
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
